import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

const REF_KEY = "me_ref_code";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  emailConfirmed: boolean;
  profileComplete: boolean | null;
  checkProfileComplete: () => Promise<boolean>;
  signInWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (
    email: string,
    password: string,
    fullName: string,
    phone: string,
  ) => Promise<{ needsEmailConfirmation: boolean }>;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [profileComplete, setProfileComplete] = useState<boolean | null>(null);

  const checkEmailConfirmed = (currentUser: User | null): boolean => {
    if (!currentUser) return false;
    return !!currentUser.email_confirmed_at;
  };

  const checkProfileComplete = useCallback(async (): Promise<boolean> => {
    if (!user) return false;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, display_name, phone')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        console.error('Error checking profile completeness:', error);
        return false;
      }

      const isComplete = !!data && !!data.display_name && !!data.phone;
      setProfileComplete(isComplete);
      return isComplete;
    } catch (err) {
      console.error('Unexpected error checking profile completeness:', err);
      return false;
    }
  }, [user]);

  // Aplica referral salvo no localStorage (se existir) após login
  const applyReferralIfAny = useCallback(async (currentSession: Session | null) => {
    try {
      if (!currentSession?.user) return;

      const code = localStorage.getItem(REF_KEY);
      if (!code) return;

      const { error } = await supabase.rpc("create_referral_by_code", { p_code: code });
      if (error) {
        console.error("Failed to apply referral:", error);
        return; // não limpa para tentar novamente depois (ex: rede)
      }

      // sucesso (ou ignorou código inválido sem erro)
      localStorage.removeItem(REF_KEY);
    } catch (e) {
      console.error("Unexpected error applying referral:", e);
    }
  }, []);

  // Initialise session on mount and listen for auth changes
  useEffect(() => {
    supabase.auth
      .getSession()
      .then(async ({ data: { session: currentSession } }) => {
        setSession(currentSession);
        setUser(currentSession?.user ?? null);
        setEmailConfirmed(checkEmailConfirmed(currentSession?.user ?? null));
        setLoading(false);

        // se já tiver sessão, tenta aplicar referral
        await applyReferralIfAny(currentSession);
      })
      .catch((e) => {
        console.error('Failed to get initial session:', e);
        setLoading(false);
      });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, currentSession) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setEmailConfirmed(checkEmailConfirmed(currentSession?.user ?? null));
      setLoading(false);

      // sempre que logar, tenta aplicar referral
      await applyReferralIfAny(currentSession);
    });

    return () => subscription.unsubscribe();
  }, [applyReferralIfAny]);

  // Verify profile completeness when user or email confirmation status changes
  useEffect(() => {
    if (user && emailConfirmed) {
      void checkProfileComplete();
    } else {
      setProfileComplete(null);
    }
  }, [user, emailConfirmed, checkProfileComplete]);

  const signInWithEmail = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUpWithEmail = async (
    email: string,
    password: string,
    fullName: string,
    phone: string,
  ) => {
    const redirectTo = `${window.location.origin}${__BASE_PATH__}/complete-profile`;

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectTo,
        data: {
          full_name: fullName,
          display_name: fullName,
          phone,
        },
      },
    });

    if (error) throw error;

    const needsEmailConfirmation = !!data?.user && !data.session;

    // Se já vier sessão (raramente), aplica referral agora
    if (data.session) {
      await applyReferralIfAny(data.session);
    }

    return { needsEmailConfirmation };
  };

  const signInWithGoogle = async () => {
    const redirectTo = `${window.location.origin}${__BASE_PATH__}/complete-profile`;

    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo },
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfileComplete(null);
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    emailConfirmed,
    profileComplete,
    checkProfileComplete,
    signInWithEmail,
    signUpWithEmail,
    signInWithGoogle,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}